import allowedFileTypes from './shared/allowed-file-types.json';
export const ALL_MIME_TYPES: string[] = allowedFileTypes.mimeTypes;
/* eslint-disable max-lines */
import type { ParseKeys } from 'i18next';
import React, { ChangeEvent, ChangeEventHandler } from 'react';
import { GroupBase } from 'react-select';
import {
  PermissionsType,
  ReportStatus,
  ReportSubstantiation,
  ReportType,
  SystemRolesName,
  SystemRolesType,
  UpdateReport,
} from './api';
import type { CaseInitialState } from './components/pages/Case/Case.slice';
import { hiddenDots } from './components/pages/Case/helpers/hiddenDots';
import { UserInitialState } from './components/pages/Users/User.slice';
import * as Integration from './data-integration/types';
import {
  AccountInputType,
  BulkImportCases,
  BulkImportCasesDynamic,
  BulkReinviteCreateUser,
  CallRecordingUrlType,
  CategoryDisplayType,
  Maybe,
  MiReportSource,
  TimeframeInput,
  UploadType,
  UserType,
} from './graphql';
import { MCCState } from './reducers/ManualCase';

declare module 'react-select/dist/declarations/src/Select' {
  export interface Props<
    Option,
    IsMulti extends boolean,
    Group extends GroupBase<Option>,
  > {
    buttonText?: string;
    routing?: boolean;
    disabled?: boolean;
    'data-testid'?: string;
  }
}
export enum UserSource {
  MANUAL_CASE = 'manual-case-creation',
  INTERNAL = 'INTERNAL',
  PUBLIC = 'PUBLIC',
  GUEST_USER = 'GUEST_USER',
}

export type User = {
  readonly name: string;
  readonly email: string;
  readonly contactEmail?: string;
  readonly role: string;
  readonly department: string;
  readonly eid: string;
  readonly id?: number;
  readonly country?: string;
  readonly displayName?: string | null;
  readonly site: string;
  readonly available?: boolean;
  readonly permissions: Array<PermissionsType>;
  readonly Accounts: AccountInputType[];
  readonly status?: string;
  readonly expiresAt?: string | null;
  readonly terminationdate?: string | null;
  readonly systemPermissions?: string[];
  readonly SystemRoles: SystemRolesType[];
  readonly source?: UserSource;
  readonly translationEnabled: boolean;
};

export interface Validation {
  name: boolean;
  email: boolean;
  role: boolean;
  department: boolean;
  site: boolean;
  eid: boolean;
  country: boolean;
  displayName: boolean;
}

export type CaseDocuments = {
  readonly s3path: string;
  readonly filename: string;
  readonly size: number;
  readonly mimeType: string;
  readonly scanStatus: UploadScanStatus | null;
};

export type Message = {
  state: MessageState;
  channel: string;
  value: string;
  author: string;
  createdAt: string;
  updatedAt: string;
  id: string;
  isReporter: boolean;
  translated: boolean;
  deletedAt?: string | null;
  deletedBy?: string | null;
};

export type CategorisedType = {
  id: number;
  name: string;
  description?: string;
  requestOpenPerpetrators?: boolean | null;
};

export type AuditEntry = {
  id: string;
  user: {
    id: string;
    name: string;
  };
  organisation: string;
  action: string;
  completed: boolean;
  createdAt: string;
  updatedAt: string;
  data: {
    report?: string;
    user?: string;
    old?: string;
    new?: string;
    data?: {
      report?: string;
    };
  };
};

export type UserDetailAuditEntry = {
  key: string;
  old: string | undefined;
  new: string | undefined;
};

export enum AuthenticationType {
  VAULT_LOGIN = 'VAULT_LOGIN',
  VAULT_2FA = 'VAULT_2FA',
  OKTA = 'OKTA',
  OKTA_SAML = 'OKTA_SAML',
  ONE_LOGIN = 'ONE_LOGIN',
  ONE_LOGIN_SAML = 'ONE_LOGIN_SAML',
  AZURE_SAML = 'AZURE_SAML',
  D1P_OIDC = 'D1P_OIDC',
}

export enum SUBMISSION_MODE {
  APP_GO_TOGETHER = 1,
  APP_GO_AHEAD = 2,
  APP_GO_ANONYMOUS = 3,
  OPEN_REPORTING = 4,
  MCC = 5,
  VAULT_TALK = 6,
  SLACK_GO_AHEAD = 7,
  SLACK_GO_ANONYMOUS = 8,
  BULK_IMPORT = 9,
  COI = 10,
  REPORTER_PORTAL = 11,
  REPORTER_PORTAL_GO_TOGETHER = 12,
}

export const readableAuthName = (authType: AuthenticationType) => {
  switch (authType) {
    case AuthenticationType.VAULT_LOGIN:
      return 'Email & Password';
    case AuthenticationType.VAULT_2FA:
      return 'Two Factor Authentication (2FA)';
    case AuthenticationType.OKTA:
      return 'Okta - OpenID Connect';
    case AuthenticationType.ONE_LOGIN:
      return 'OneLogin - OpenID Connect';
    case AuthenticationType.OKTA_SAML:
      return 'Okta - SAML';
    case AuthenticationType.ONE_LOGIN_SAML:
      return 'OneLogin - SAML';
    case AuthenticationType.AZURE_SAML:
      return 'Azure - SAML';
    case AuthenticationType.D1P_OIDC:
      return 'Diligent One Platform (D1P)';
    default:
      return authType;
  }
};

export const isVaultAuth = (authType: AuthenticationType) =>
  authType === AuthenticationType.VAULT_LOGIN ||
  authType === AuthenticationType.VAULT_2FA;

export const isSAMLAuth = (authType: AuthenticationType) =>
  authType === AuthenticationType.ONE_LOGIN_SAML ||
  authType === AuthenticationType.OKTA_SAML ||
  authType === AuthenticationType.AZURE_SAML;

export const isOIDCAuth = (authType: AuthenticationType) =>
  authType === AuthenticationType.ONE_LOGIN ||
  authType === AuthenticationType.OKTA ||
  authType === AuthenticationType.D1P_OIDC;

export enum AuditActions {
  ADD_CASE_CATEGORY = 'ADD_CASE_CATEGORY',
  ADD_CASE_TAG = 'ADD_CASE_TAG',
  ADD_EXTERNAL_OFFENDER = 'ADD_EXTERNAL_OFFENDER',
  ADD_INTERNAL_OFFENDER = 'ADD_INTERNAL_OFFENDER',
  ADD_REPORT_UPLOADS_POST_SUBMISSION = 'ADD_REPORT_UPLOADS_POST_SUBMISSION',
  CHANGE_CASE_LEAD = 'CHANGE_CASE_LEAD',
  CHANGE_CASE_STATUS = 'CHANGE_CASE_STATUS',
  CHANGE_DETERMINED_CATEGORY = 'CHANGE_DETERMINED_CATEGORY',
  CHANGE_REPORT_ASSIGNEE = 'CHANGE_REPORT_ASSIGNEE',
  CHANGE_REPORT_STATUS = 'CHANGE_REPORT_STATUS',
  CHANGE_REPORT_SUBSTANTIATION = 'CHANGE_REPORT_SUBSTANTIATION',
  CREATE_CASE = 'CREATE_CASE',
  CREATE_CASE_DOCUMENT = 'CREATE_CASE_DOCUMENT',
  CREATE_CASE_DOCUMENT_ATTACHMENT = 'CREATE_CASE_DOCUMENT_ATTACHMENT',
  CREATE_CASE_LINK = 'CREATE_CASE_LINK',
  CREATE_DRAFT = 'CREATE_DRAFT',
  CREATE_MESSAGE = 'CREATE_MESSAGE',
  CREATE_REPORT_NOTE = 'CREATE_REPORT_NOTE',
  CREATE_ROUTING_RULE = 'CREATE_ROUTING_RULE',
  DATA_SANITISATION_EVIDENCE = 'DATA_SANITISATION_EVIDENCE',
  DATA_SANITISATION_MESSAGES = 'DATA_SANITISATION_MESSAGES',
  DATA_SANITISATION_NAMED_OFFENDERS = 'DATA_SANITISATION_NAMED_OFFENDERS',
  DATA_SANITISATION_REPORT_DETAILS = 'DATA_SANITISATION_REPORT_DETAILS',
  DATA_SANITISATION_REPORTER = 'DATA_SANITISATION_REPORTER',
  DATA_SANITISATION_REPORTER_NOTE = 'DATA_SANITISATION_REPORTER_NOTE',
  DELETE_CASE_CATEGORY = 'DELETE_CASE_CATEGORY',
  DELETE_CASE_DOCUMENT_ATTACHMENT = 'DELETE_CASE_DOCUMENT_ATTACHMENT',
  DELETE_CASE_DOCUMENTS = 'DELETE_CASE_DOCUMENTS',
  DELETE_CASE_LINK = 'DELETE_CASE_LINK',
  DELETE_REPORT_NOTE = 'DELETE_REPORT_NOTE',
  DELETE_ROUTING_RULE = 'DELETE_ROUTING_RULE',
  DISABLE_ROUTING_RULE = 'DISABLE_ROUTING_RULE',
  DOWNLOAD_INSIGHTS_CHART = 'DOWNLOAD_INSIGHTS_CHART',
  ENABLE_ROUTING_RULE = 'ENABLE_ROUTING_RULE',
  EXPORT_REPORT = 'EXPORT_REPORT',
  GET_REPORT_EVIDENCE = 'GET_REPORT_EVIDENCE',
  INHERIT_CASE_CATEGORY = 'INHERIT_CASE_CATEGORY',
  INVITE_TO_CASE = 'INVITE_TO_CASE',
  LINK_INTERNAL_OFFENDER = 'LINK_INTERNAL_OFFENDER',
  PUBLIC_SUBMIT_REPORT = 'PUBLIC_SUBMIT_REPORT',
  REMOVE_CASE_TAG = 'REMOVE_CASE_TAG',
  REMOVE_FROM_CASE = 'REMOVE_FROM_CASE',
  RETURNED_DEFAULT_ASSIGNEE = 'RETURNED_DEFAULT_ASSIGNEE',
  RETURNED_RANDOM_ASSIGNEE = 'RETURNED_RANDOM_ASSIGNEE',
  RETURNED_ROUTING_ASSIGNEE = 'RETURNED_ROUTING_ASSIGNEE',
  SUBMIT_REPORT = 'SUBMIT_REPORT',
  TRANSLATE_REPORT = 'TRANSLATE_REPORT',
  UPDATE_CASE_CLOSED_AT_DATE = 'UPDATE_CASE_CLOSED_AT_DATE',
  UPDATE_CASE_COMPANY_CREATED_AT_DATE = 'UPDATE_CASE_COMPANY_CREATED_AT_DATE',
  UPDATE_CASE_COUNTRY_REGION = 'UPDATE_CASE_COUNTRY_REGION',
  UPDATE_CASE_DESCRIPTION = 'UPDATE_CASE_DESCRIPTION',
  UPDATE_CASE_DOCUMENT = 'UPDATE_CASE_DOCUMENT',
  UPDATE_CASE_INVESTIGATING_AT_DATE = 'UPDATE_CASE_INVESTIGATING_AT_DATE',
  UPDATE_CASE_READ_AT_DATE = 'UPDATE_CASE_READ_AT_DATE',
  UPDATE_CASE_TEAM_MEMBER_ACCESS = 'UPDATE_CASE_TEAM_MEMBER_ACCESS',
  SET_CASE_CUSTOM_FIELD_VALUE = 'SET_CASE_CUSTOM_FIELD_VALUE',
  UPDATE_CASE_TITLE = 'UPDATE_CASE_TITLE',
  UPDATE_REPORT = 'UPDATE_REPORT',
  UPDATE_REPORT_RELATIONSHIP_TO_ORGANISATION = 'UPDATE_REPORT_RELATIONSHIP_TO_ORGANISATION',
  UPDATE_REPORT_SUBMITTED_AT_DATE = 'UPDATE_REPORT_SUBMITTED_AT_DATE',
  UPDATE_ROUTING_RULE = 'UPDATE_ROUTING_RULE',
  UPDATE_ROUTING_RULE_PRIORITIES = 'UPDATE_ROUTING_RULE_PRIORITIES',
  UPDATE_USER_DETAILS = 'UPDATE_USER_DETAILS',
  UPDATE_USER_PERMISSIONS = 'UPDATE_USER_PERMISSIONS',
  VIEW_CASE = 'VIEW_CASE',
  VIEW_CASE_DOCUMENT = 'VIEW_CASE_DOCUMENT',
  VIEW_CASE_DOCUMENT_ATTACHMENT = 'VIEW_CASE_DOCUMENT_ATTACHMENT',
  VIEW_REPORT_EVIDENCE = 'VIEW_REPORT_EVIDENCE',
  VIEW_REPORT_MESSAGES = 'VIEW_REPORT_MESSAGES',
  IMPORT_CASE = 'IMPORT_CASE',
}

export const caseAuditActions = [
  AuditActions.ADD_CASE_CATEGORY,
  AuditActions.ADD_CASE_TAG,
  AuditActions.CHANGE_CASE_LEAD,
  AuditActions.CHANGE_CASE_STATUS,
  AuditActions.CREATE_CASE,
  AuditActions.IMPORT_CASE,
  AuditActions.CREATE_CASE_DOCUMENT,
  AuditActions.CREATE_CASE_DOCUMENT_ATTACHMENT,
  AuditActions.CREATE_CASE_LINK,
  AuditActions.DELETE_CASE_CATEGORY,
  AuditActions.DELETE_CASE_DOCUMENT_ATTACHMENT,
  AuditActions.DELETE_CASE_DOCUMENTS,
  AuditActions.DELETE_CASE_LINK,
  AuditActions.INHERIT_CASE_CATEGORY,
  AuditActions.INVITE_TO_CASE,
  AuditActions.REMOVE_FROM_CASE,
  AuditActions.REMOVE_CASE_TAG,
  AuditActions.RETURNED_DEFAULT_ASSIGNEE,
  AuditActions.RETURNED_RANDOM_ASSIGNEE,
  AuditActions.RETURNED_ROUTING_ASSIGNEE,
  AuditActions.UPDATE_CASE_CLOSED_AT_DATE,
  AuditActions.UPDATE_CASE_COMPANY_CREATED_AT_DATE,
  AuditActions.UPDATE_CASE_COUNTRY_REGION,
  AuditActions.UPDATE_CASE_DESCRIPTION,
  AuditActions.UPDATE_CASE_DOCUMENT,
  AuditActions.UPDATE_CASE_INVESTIGATING_AT_DATE,
  AuditActions.UPDATE_CASE_READ_AT_DATE,
  AuditActions.UPDATE_CASE_TEAM_MEMBER_ACCESS,
  AuditActions.SET_CASE_CUSTOM_FIELD_VALUE,
  AuditActions.UPDATE_CASE_TITLE,
  AuditActions.VIEW_CASE,
  AuditActions.VIEW_CASE_DOCUMENT,
  AuditActions.VIEW_CASE_DOCUMENT_ATTACHMENT,
];

export enum UploadErrors {
  BAD_FILE_EXTENSION = 0,
  BAD_FILE_HEADERS = 1,
  BAD_TERMINATION_DATES = 2,
  CSV_PARSING_ERROR = 3,
  GENERIC_ERROR = 4,
}

export enum MessageState {
  NEW = 0,
  SENT = 1,
  READ = 2,
}

export enum SaveDocumentButtonStates {
  UNSAVED = 'Save changes',
  SAVING = 'Saving...',
  SAVED = 'Saved to docs',
}

export enum AccountType {
  Portal = 'portal',
  App = 'app',
}

export type PublicReportingAccount = {
  user: {
    id: string;
    organisation: {
      id: number;
      name: string;
      expiryPeriod: number;
      slug?: string;
    };
    permissions: PermissionsType[];
    translationEnabled?: boolean;
  };
  username: string;
  id: string;
  displayName: string;
  rpDisplayName: string;
};

/**
 * Used in REST API for response from report submission, rather than fetch
 */
export interface PublicReportSubmission {
  category: string;
  report: string;
  case?: string;
  status: string;
  Organisation?: Organisation;
  submissionMode?: number;
  assignee?: {
    id?: number;
    name: string;
    displayName?: string;
  };
  editable?: boolean;
  submittedAt?: Date;
  substantiation?: string;
  existingReport?: boolean;
}

export type Report = {
  readonly reporter: string;
  readonly date: string;
  readonly reportId: string;
  readonly type: string;
  readonly reportedCategory: CategorisedType;
  readonly determinedCategory: CategorisedType;
  readonly departments: Array<string>;
  readonly status: ReportStatus;
  readonly category: string;
  readonly displayId: string;
  readonly assignee: string;
  readonly public: string;
  readonly source: string;
  readonly anonymous?: boolean;
};

export type Case = {
  readonly id: string;
  readonly name: string | null | undefined;
  readonly submissionType: CategorisedType;
  readonly updatedAt: string;
  readonly createdAt: string;
  readonly status: ReportStatus;
  readonly caseLead: string;
  readonly reporter: string;
  readonly source: string;
  CaseAttribute?: {
    readAt?: string | null;
    investigatingAt?: string | null;
    closedAt?: string | null;
  };
};

type RelationshipTypeLocale = {
  locale: string;
  name: string;
};

export type RelationshipType = {
  id: number;
  order: number;
  relationshipTypeLocales: RelationshipTypeLocale[];
};

export type PublicReport = {
  name?: string;
  email?: string;
  password: string;
  confirmation: string;
  organisation: string;
  logo?: string;
  relationshipToCompany: number | null;
  relationshipType?: string;
  perpetrators: {
    dataDeletedAt?: string | null;
    external: boolean;
    name: string;
  }[];
  evidence: {
    events: [
      {
        created: Date;
        id: string;
        report: string;
        updated: Date;
      },
    ];
    evidence: {
      created: Date;
      id: string;
      event: string;
      report: string;
      type: string;
      updated: Date;
      value:
        | string
        | {
            name: string;
            external: boolean;
            dataDeletedAt?: string | null;
            scanStatus?: UploadScanStatus;
            path?: string;
            fileSize?: number;
            fileExtension?: string;
          };
    }[];
    report: {
      anonymous?: boolean;
      category: string;
      reportedCategory?: number;
      created: Date;
      id: string;
      status: string;
      updated: Date;
      relationshipToCustomer: string;
    };
  };
  reporter?: { id: string; name: string; email: string };
  anonymous?: boolean;
  category: string;
  reportedCategory?: number | { name: string; id: number };
  issueType?: CategorisedType;
  created: Date;
  id: string;
  status: string;
  updated: Date;
  source: string;
  audioRecordings?: CallRecordingUrlType[];
  phoneLineType?: PhoneLineType;
  postSubmissionFiles?: UploadType[];
  privacyStatementUrl?: string | null;
  selectedCountryName?: string;
  countries?: Array<{ name: string }>;
  selectedSiteName?: string;
  sites?: Array<{ name: string }>;
};

export const getReadableIdWithDash = (reportId: string | null | undefined) => {
  if (reportId && reportId.length > 8) {
    return `${reportId.substring(0, 4)}-${reportId.substring(4, 8)}`;
  } else {
    return '';
  }
};

export const getReadableId = (caseId: string | null | undefined) => {
  if (caseId && caseId.length > 8) {
    return caseId.substring(0, 8);
  } else {
    return '';
  }
};

export const getCaseReadableId = (
  caseId: string | null | undefined,
  viewPermission: boolean = true,
) => {
  if (!viewPermission) return hiddenDots;

  return getReadableId(caseId);
};

export type Feature = {
  readonly key: FeatureFlag;
  readonly enabled: boolean;
  readonly organisation?: number;
};

export enum FeatureFlag {
  ANONYMOUS = 'anonymous',
  ADD_NAMED_OFFENDER = 'add-named-offender',
  BULK_ACTIVATE_WITHOUT_INVITE = 'bulk-activate-without-invite',
  CASE_CLOSE_COUNTRY_REGION = 'case-close-country-region',
  CASE_DATA_DOWNLOAD = 'case-data-download',
  CASE_IMPORTER = 'case-importer',
  CASE_IMPORTER_V2 = 'case-importer-v2',
  COOKIE_CONSENT = 'cookie-consent',
  CUSTOM_USER_FIELDS = 'custom-user-fields',
  DATA_INTEGRATION = 'data-integration',
  DYNAMIC_TRANSLATIONS = 'dynamic-translations',
  FRONTEND_PENDO_TRACKING = 'frontend-pendo-tracking',
  FRONTEND_SEGMENT_TRACKING = 'frontend-segment-tracking',
  GO_TOGETHER = 'go-together',
  GROUPS = 'groups',
  HRIS_SYNC = 'hris-sync',
  LINE_MANAGEMENT = 'line-management',
  OPR_DEFAULT_ASSIGNEE_ROUTING = 'opr-default-assignee-routing',
  OPR_RESKIN = 'opr-reskin',
  PDF_EXPORT = 'pdf-export',
  PUBLIC_REPORTING = 'public-reporting',
  REPORTER_VIEW_EVIDENCE_DISABLED = 'reporter-view-evidence-disabled',
  ROUTING = 'routing',
  ROUTING_COMPANY_RELATIONSHIP = 'routing-company-relationship',
  SLACK_REPORTING = 'slack-reporting',
  USE_NORMALIZED_REPORT_DATA = 'use-normalized-report-data',
  VAULT_TALK = 'vault-talk',
  DATA_DELETION = 'data-deletion',
  APP_REPORTING = 'app-reporting',
  RELATIONSHIP_TO_ORG = 'relationship-to-org',
  OPR_PASSWORD_RESET = 'opr-password-reset',
  LINKING_CASES = 'linking-cases',
  REPORT_INTAKE_IMPROVEMENTS = 'report-intake-improvements',
  REPORT_INTAKE_IMPROVEMENTS_DRAFT = 'report-intake-improvements-draft',
  CONFLICT_OF_INTEREST = 'conflict-of-interest',
  COUNTRIES_AND_REGIONS = 'countries-and-regions',
  DYNAMIC_QUESTIONS_V1 = 'dynamic-questions-v1',
  ROLE_CHANGE_NOTIFICATION = 'role-change-notification',
  ATLAS_THEME = 'atlas-theme',
  ATLAS_GLOBAL_NAV = 'atlas-global-nav',
  DATA_RETENTION = 'data-retention',
  OPR_COUNTRY = 'opr-country',
  OPR_SITE = 'opr-site',
  CATEGORY_CONFIG_REDESIGN = 'category-config-redesign',
  SPEAK_UP = 'speak-up',
  AI_INVESTIGATOR = 'ai-investigator',
  CUSTOM_FIELDS = 'custom-fields',
  FUZZY_MATCHING_NAMED_OFFENDERS = 'fuzzy-matching-named-offenders',
}

export type Organisation = {
  readonly name: string;
  readonly id: string;
  readonly expiryPeriod: number;
  readonly slug?: string;
  readonly authenticationType: AuthenticationType;
  readonly activeScimProvider?: boolean;
  readonly isLegacyMode?: boolean;
  readonly authenticationAdmin?: AuthenticationAdmin;
  readonly d1pHomeUrl?: string | null;
};

export type OauthProviderType = {
  issuer: string;
  client_id: string;
  client_secret: string;
};
export type SamlProviderType = {
  issuer: string;
  ssoURL: string;
  certificate: string;
};

export type AuthenticationAdmin = {
  oauthProvider?: OauthProviderType;
  samlProvider?: SamlProviderType;
  organisation: {
    authenticationType: AuthenticationType;
    slug?: string;
  };
  auditEntry?: {
    user: {
      name: string;
      role: string;
    };
    createdAt: string;
  };
  scimProvider?: {
    provider: string;
    active: boolean;
    secretLocation?: string;
    secretLocationKey?: string;
  };
};

export enum UserTypes {
  FETCH_ONE = '@user',
  LOGIN = '@login',
  UPDATE_AVAILABILITY = '@user/availability',
  UPDATE_TRANSLATION_ENABLED = '@user/translationEnabled',
  SESSION = '@session',
  CLEAR_SESSION = '@session/clear',
}

export enum LoginFlow {
  USERNAME = 'username',
  PASSWORD = 'password',
  CODE = 'code',
  SSO = 'sso',
}

export enum LoginTypes {
  FLOW = '@login/flow',
  EMAIL = '@login/email',
}

export type LoginAction =
  | {
      type: LoginTypes.FLOW;
      payload: LoginFlow;
    }
  | {
      type: LoginTypes.EMAIL;
      payload: string;
    };

export enum FeatureTypes {
  FETCH_ALL = '@features',
}

export type ColumnPreferences = {
  type: boolean;
  dateReceived: boolean;
  status: boolean;
  assignee: boolean;
  reporter: boolean;
  source: boolean;
  substantiation: boolean;
};

export enum ReportTypes {
  UPDATE_STATUS = '@report/updateStatus',
  UPDATE_ISSUE_TYPE = '@report/updateIssueType',
  SET_COLUMN_SHOWING = '@report/setColumnShowing',
  FETCH_ONE = '@report',
  MESSAGES = '@report/messages',
  MESSAGE = '@report/message',
  UPDATE_SUBSTANTIATION = '@report/updateSubstantiation',
}

export enum OpenReportTypes {
  RESET = '@openReport/reset',
  PHONENUMBER_RESET = '@openReport/phoneNumberReset',
  CLEAR = '@openReport/clear',
  SELECT_SUBMISSION_TYPE = '@openReport/selectSubmissionType',
  SELECT_RELATIONSHIP_TYPE = '@openReport/selectRelationshipType',
  SET_EVENT_DATE = '@openReport/setEventDate',
  SET_EVENT_TIME = '@openReport/setEventTime',
  SET_EVENT_DESCRIPTION = '@openReport/setEventDescription',
  SET_EVENT_EMOTION = '@openReport/setEventEmotion',
  SET_EMAIL = '@openReport/setEmail',
  SET_NAME = '@openReport/setName',
  SET_PASSWORD = '@openReport/setPassword',
  SET_CONFIRMATION = '@openReport/setConfirmation',
  SET_CURRENT_PASSWORD = '@openReport/setCurrentPassword',
  SET_NEW_PASSWORD = '@openReport/setNewPassword',
  SET_CONFIRM_NEW_PASSWORD = '@openReport/setConfirmNewPassword',
  SET_EVENT_PERPETRATORS = '@openReport/setEventPerpetrators',
  SET_EVENT_WITNESSES = '@openReport/setEventWitness',
  SET_EVENT_LOCATION = '@openReport/setEventLocation',
  SET_SELECTED_COUNTRY = '@openReport/setSelectedCountry',
  SET_ORGANISATION_COUNTRIES = '@openReport/setOrganisationCountries',
  SET_SELECTED_SITE = '@openReport/setSelectedSite',
  SET_ORGANISATION_SITES = '@openReport/setOrganisationSites',
  SET_LOGIN_USERNAME = '@openReport/setLoginUsername',
  SET_LOGIN_PASSWORD = '@openReport/setLoginPassword',
  SET_LOGIN_PHONENUMBER = '@openReport/setLoginPhoneNumber',
  SET_LOGIN_PIN = '@openReport/setLoginPin',
  SET_NOTFOUND = '@openReport/setNotFound',
  SET_LOADING = '@openReport/setLoading',
  SET_ORGANISATION = '@openReport/setOrganisation',
  SET_ORGANISATION_LOGO = '@openReport/setOrganisationLogo',
  SET_ORGANISATION_PRIVACY_STATEMENT = '@openReport/setOrganisationPrivacyStatement',
  ON_SUBMIT = '@openReport/onSubmit',
  SET_IS_SUBMITTING = '@openReport/setIsSubmitting',
  SET_UPLOAD_ERRORS = '@openReport/setUploadErrors',
  SET_POST_SUBMISSION_IS_SUBMITTING = '@openReport/setPostSubmissionIsSubmitting',
  SET_POST_SUBMISSION_UPLOAD_ERRORS = '@openReport/setPostSubmissionUploadErrors',
  ON_CHANGE_PASSWORD = '@openReport/onChangePassword',
  FETCH_OPEN_REPORT = '@openReport/fetchOpenReport',
  FETCH_ALL_PUBLIC = '@openReport/fetchAllPublic',
  MESSAGES = '@openReport/allMessages',
  MESSAGE = '@openReport/message',
  READ_MESSAGE = '@openReport/readMessage',
  TRANSLATE_MESSAGES = '@openReport/translateMessages',
  SET_TRANSLATION_ENABLED = '@openReport/setTranslationEnabled',
  SESSION = '@openReport/session',
  SET_SOLUTION_TOKEN = '@openReport/setSolutionToken',
  SET_FETCHED_CHALLENGE = '@openReport/setFetchedChallenge',
  SET_USER_ANSWER = '@openReport/setUserAnswer',
  SET_NAV_MENU_SIZE = '@openReport/setNavMenuSize',
  SET_REPORT_JUST_SUBMITTED = '@openReport/setReportJustSubmitted',
}

export enum FetchingType {
  FETCHING = '@fetching',
  FETCHING_COMPLETED = '@fetchingCompleted',
}

export enum OrganisationTypes {
  FETCH = '@organisation/fetch',
  SLUG = '@organisation/slug',
  FETCH_AUTH_ADMIN = '@organisation/fetchAuthAdmin',
  FETCH_AUTH_TYPE = '@organisation/fetchAuthType',
  UPDATE_AUTH_TYPE = '@organisation/updateAuthType',
}

export enum DataIntegrationTypes {
  UPDATE_STATUS = '@integration/updateStatus',
  UPDATE_INVITE_NEW = '@integration/updateInviteNew',
}

export enum InsightsGlobalFilterTypes {
  UPDATE_ORGANISATIONAL_SITES = 'updateOrganisationalSites',
  UPDATE_ORGANISATIONAL_COUNTRIES = 'updateOrganisationalCountries',
  UPDATE_ORGANISATIONAL_CASE_COUNTRY_CODES = 'updateOrganisationalCaseCountryCodes',
  UPDATE_ORGANISATIONAL_CASE_REGION_IDS = 'updateOrganisationalCaseRegionIds',
  UPDATE_ORGANISATIONAL_TIME = 'updateOrganisationalTime',
  UPDATE_ORGANISATIONAL_GLOBAL_LOCATION_FILTER = 'updateOrganisationalGlobalLocationFilter',
  UPDATE_ORGANISATIONAL_LOCATION_FILTER_CATEGORY = 'updateOrganisationalLocationFilterCategory',
  UPDATE_OPERATIONAL_SITES = 'updateOperationalSites',
  UPDATE_OPERATIONAL_COUNTRIES = 'updateOperationalCountries',
  UPDATE_OPERATIONAL_CASE_COUNTRY_CODES = 'updateOperationalCaseCountryCodes',
  UPDATE_OPERATIONAL_CASE_REGION_IDS = 'updateOperationalCaseRegionIds',
  UPDATE_OPERATIONAL_TIME = 'updateOperationalTime',
  UPDATE_TOP_DEPARTMENTS = 'updateTopDepartments',
  UPDATE_OPERATIONAL_GLOBAL_LOCATION_FILTER = 'updateOperationalGlobalLocationFilter',
  UPDATE_OPERATIONAL_LOCATION_FILTER_CATEGORY = 'updateOperationalLocationFilterCategory',
}

export type DataIntegrationAction =
  | {
      type: DataIntegrationTypes.UPDATE_STATUS;
      payload: Integration.Status;
    }
  | {
      type: DataIntegrationTypes.UPDATE_INVITE_NEW;
      payload: boolean;
    };

export type OrganisationAction =
  | {
      type: OrganisationTypes.FETCH;
      payload: Organisation;
    }
  | {
      type: OrganisationTypes.FETCH_AUTH_TYPE;
      payload: {
        authenticationType: AuthenticationType;
      };
    }
  | {
      type: OrganisationTypes.FETCH_AUTH_ADMIN;
      payload: AuthenticationAdmin;
    }
  | {
      type: OrganisationTypes.UPDATE_AUTH_TYPE;
      payload: {
        slug: string;
        authenticationType: AuthenticationType;
        authenticationAdmin: AuthenticationAdmin;
      };
    };

export type FeaturesAction = {
  type: FeatureTypes.FETCH_ALL;
  payload: Feature[];
};

export type FetchingAction =
  | {
      type: FetchingType.FETCHING;
      payload: boolean;
    }
  | {
      type: FetchingType.FETCHING_COMPLETED;
      payload: boolean;
    };

export type UserAction =
  | {
      type: UserTypes.LOGIN;
      payload: User;
    }
  | {
      type: UserTypes.UPDATE_AVAILABILITY;
      payload: boolean;
    }
  | {
      type: UserTypes.UPDATE_TRANSLATION_ENABLED;
      payload: boolean;
    }
  | {
      type: UserTypes.SESSION;
      payload: {
        session: string;
        hash: string;
      };
    }
  | {
      type: UserTypes.CLEAR_SESSION;
    };

export type OpenReportAction =
  | {
      type: OpenReportTypes.RESET;
    }
  | {
      type: OpenReportTypes.PHONENUMBER_RESET;
    }
  | {
      type: OpenReportTypes.CLEAR;
    }
  | {
      type: OpenReportTypes.SELECT_SUBMISSION_TYPE;
      payload: CategorisedType;
    }
  | {
      type: OpenReportTypes.SELECT_RELATIONSHIP_TYPE;
      payload: CategorisedType;
    }
  | {
      type: OpenReportTypes.SET_EVENT_DATE;
      payload: string;
    }
  | {
      type: OpenReportTypes.SET_EVENT_TIME;
      payload: string;
    }
  | {
      type: OpenReportTypes.SET_EVENT_DESCRIPTION;
      payload: string;
    }
  | {
      type: OpenReportTypes.SET_EVENT_EMOTION;
      payload: string;
    }
  | {
      type: OpenReportTypes.SET_EMAIL;
      payload: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    }
  | {
      type: OpenReportTypes.SET_NAME;
      payload: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    }
  | {
      type: OpenReportTypes.SET_PASSWORD;
      payload: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    }
  | {
      type: OpenReportTypes.SET_CONFIRMATION;
      payload: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    }
  | {
      type: OpenReportTypes.SET_CURRENT_PASSWORD;
      payload: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    }
  | {
      type: OpenReportTypes.SET_NEW_PASSWORD;
      payload: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    }
  | {
      type: OpenReportTypes.SET_CONFIRM_NEW_PASSWORD;
      payload: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    }
  | {
      type: OpenReportTypes.SET_EVENT_PERPETRATORS;
      payload: string;
    }
  | {
      type: OpenReportTypes.SET_EVENT_WITNESSES;
      payload: string;
    }
  | {
      type: OpenReportTypes.SET_EVENT_LOCATION;
      payload: string;
    }
  | {
      type: OpenReportTypes.SET_SELECTED_COUNTRY;
      payload: string;
    }
  | {
      type: OpenReportTypes.SET_ORGANISATION_COUNTRIES;
      payload: Array<{ name: string }>;
    }
  | {
      type: OpenReportTypes.SET_SELECTED_SITE;
      payload: string;
    }
  | {
      type: OpenReportTypes.SET_ORGANISATION_SITES;
      payload: Array<{ name: string }>;
    }
  | {
      type: OpenReportTypes.SET_LOGIN_USERNAME;
      payload: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    }
  | {
      type: OpenReportTypes.SET_LOGIN_PASSWORD;
      payload: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    }
  | {
      type: OpenReportTypes.SET_LOGIN_PHONENUMBER;
      payload: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    }
  | {
      type: OpenReportTypes.SET_LOGIN_PIN;
      payload: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    }
  | {
      type: OpenReportTypes.SET_NOTFOUND;
      payload: boolean;
    }
  | {
      type: OpenReportTypes.SET_LOADING;
      payload: boolean;
    }
  | {
      type: OpenReportTypes.SET_ORGANISATION;
      payload: string;
    }
  | {
      type: OpenReportTypes.SET_ORGANISATION_LOGO;
      payload: string;
    }
  | {
      type: OpenReportTypes.SET_ORGANISATION_PRIVACY_STATEMENT;
      payload: string | null;
    }
  | {
      type: OpenReportTypes.ON_SUBMIT;
      payload: {
        publicReporterAccount: PublicReportingAccount;
        redirect: string;
        loginValues: {
          username: string;
        };
      };
    }
  | {
      type: OpenReportTypes.SET_IS_SUBMITTING;
      payload: boolean;
    }
  | {
      type: OpenReportTypes.SET_POST_SUBMISSION_IS_SUBMITTING;
      payload: boolean;
    }
  | {
      type: OpenReportTypes.SET_UPLOAD_ERRORS;
      payload: number;
    }
  | {
      type: OpenReportTypes.SET_POST_SUBMISSION_UPLOAD_ERRORS;
      payload: number;
    }
  | {
      type: OpenReportTypes.ON_CHANGE_PASSWORD;
    }
  | {
      type: OpenReportTypes.FETCH_ALL_PUBLIC;
      payload: Report[];
    }
  | {
      type: OpenReportTypes.MESSAGES;
      payload: { reportId: string; messages: Array<Message> };
    }
  | {
      type: OpenReportTypes.MESSAGE;
      payload: Message;
    }
  | {
      type: OpenReportTypes.READ_MESSAGE;
      payload: Message;
    }
  | {
      type: OpenReportTypes.TRANSLATE_MESSAGES;
      payload: boolean;
    }
  | {
      type: OpenReportTypes.SET_TRANSLATION_ENABLED;
      payload: boolean;
    }
  | {
      type: OpenReportTypes.SESSION;
      payload: Session;
    }
  | {
      type: OpenReportTypes.SET_FETCHED_CHALLENGE;
      payload: object;
    }
  | {
      type: OpenReportTypes.SET_SOLUTION_TOKEN;
      payload: string;
    }
  | {
      type: OpenReportTypes.SET_NAV_MENU_SIZE;
      payload: boolean;
    }
  | {
      type: OpenReportTypes.SET_USER_ANSWER;
      payload: string;
    }
  | {
      type: OpenReportTypes.SET_REPORT_JUST_SUBMITTED;
      payload: boolean;
    };

export type ReportAction =
  | {
      type: ReportTypes.FETCH_ONE;
      payload: ReportType;
    }
  | {
      type: ReportTypes.UPDATE_STATUS;
      payload: { id: string; status: ReportStatus };
    }
  | {
      type: ReportTypes.SET_COLUMN_SHOWING;
      payload: ColumnPreferences;
    }
  | {
      type: ReportTypes.UPDATE_ISSUE_TYPE;
      payload: { reportId: string; issueType: CategorisedType };
    }
  | {
      type: ReportTypes.MESSAGES;
      payload: { reportId: string; messages: Array<Message> };
    }
  | {
      type: ReportTypes.MESSAGE;
      payload: Message;
    }
  | {
      type: ReportTypes.UPDATE_SUBSTANTIATION;
      payload: { id: string; substantiation: ReportSubstantiation };
    };

export type InsightsGlobalFilterAction =
  | {
      type: InsightsGlobalFilterTypes.UPDATE_ORGANISATIONAL_SITES;
      payload: string[];
    }
  | {
      type: InsightsGlobalFilterTypes.UPDATE_ORGANISATIONAL_COUNTRIES;
      payload: string[];
    }
  | {
      type: InsightsGlobalFilterTypes.UPDATE_ORGANISATIONAL_CASE_COUNTRY_CODES;
      payload: string[];
    }
  | {
      type: InsightsGlobalFilterTypes.UPDATE_ORGANISATIONAL_CASE_REGION_IDS;
      payload: string[];
    }
  | {
      type: InsightsGlobalFilterTypes.UPDATE_ORGANISATIONAL_TIME;
      payload: { timeOption: string; customTimeframe?: TimeframeInput };
    }
  | {
      type: InsightsGlobalFilterTypes.UPDATE_ORGANISATIONAL_GLOBAL_LOCATION_FILTER;
      payload: GlobalFilterLocation;
    }
  | {
      type: InsightsGlobalFilterTypes.UPDATE_ORGANISATIONAL_LOCATION_FILTER_CATEGORY;
      payload: LocationFilterCategory;
    }
  | {
      type: InsightsGlobalFilterTypes.UPDATE_OPERATIONAL_SITES;
      payload: string[];
    }
  | {
      type: InsightsGlobalFilterTypes.UPDATE_OPERATIONAL_COUNTRIES;
      payload: string[];
    }
  | {
      type: InsightsGlobalFilterTypes.UPDATE_OPERATIONAL_CASE_COUNTRY_CODES;
      payload: string[];
    }
  | {
      type: InsightsGlobalFilterTypes.UPDATE_OPERATIONAL_CASE_REGION_IDS;
      payload: string[];
    }
  | {
      type: InsightsGlobalFilterTypes.UPDATE_OPERATIONAL_TIME;
      payload: { timeOption: string; customTimeframe?: TimeframeInput };
    }
  | {
      type: InsightsGlobalFilterTypes.UPDATE_OPERATIONAL_GLOBAL_LOCATION_FILTER;
      payload: GlobalFilterLocation;
    }
  | {
      type: InsightsGlobalFilterTypes.UPDATE_OPERATIONAL_LOCATION_FILTER_CATEGORY;
      payload: LocationFilterCategory;
    }
  | {
      type: InsightsGlobalFilterTypes.UPDATE_TOP_DEPARTMENTS;
      payload: string[];
    };

type ChangePasswordState = {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};
type LoginState = {
  username: string;
  password: string;
};

type ReportState = {
  requestOpenPerpetrators: boolean;
  notfound: boolean;
  loading: boolean;
  perpetrators: string;
  redirect?: string;
  publicReporterAccount: PublicReportingAccount | null;
  publicReports: Report[];
  messages: Map<string, Message[]>;
  translateMessages: boolean;
  session: string;
  solutionToken: string;
  challenge: Record<string, string>;
  userAnswer: string;
  navMenu: {
    isOpen: boolean;
  };
  isSubmitting: boolean;
  fileUploadErrors: number;
  isPostFileUploadSubmitting: boolean;
  postFileUploadErrors: number;
  reportJustSubmitted: boolean;
};

type PhoneNumberLoginState = {
  phoneNumber: string;
  pin: string;
};

export enum GlobalFilterLocation {
  SITES = 'sites',
  COUNTRIES = 'countries',
  CASE_COUNTRIES = 'caseCountryCodes',
  CASE_REGIONS = 'caseRegionIds',
}

export enum LocationFilterCategory {
  NAMED_OFFENDER = 'namedOffender',
  CASE = 'case',
}

export type State = {
  globalUsers: {
    currentUser: User | null;
    session: string;
    hash: string;
    usersUpdated: Date | null;
  };
  globalReports: {
    reports: Array<Report>;
    currentReport: ReportType | null;
    columnShowing: ColumnPreferences;
    messages: Map<string, Array<Message>>;
    auditLog: Map<string, Array<AuditEntry>>;
  };
  organisation: {
    organisation: Organisation | null;
    authenticationAdmin?: AuthenticationAdmin;
    isLegacyMode: boolean;
  };
  fetching: {
    isFetching: boolean;
  };
  features: Array<Feature>;
  login: {
    flow: LoginFlow;
    email: string;
  };
  openReporting: {
    openReportReducer?: PublicReport;
    changePasswordReducer?: ChangePasswordState;
    loginReducer?: LoginState;
    openReportingReducer?: ReportState;
    phoneNumberLoginValue?: PhoneNumberLoginState;
    phoneNumberLoginValues?: PhoneNumberLoginState;
  };
  integrationStatus: Integration.Status;
  manualCaseCreation: {
    mccReducer: MCCState;
  };
  insightsGlobalFilters: {
    organisational: {
      sites?: string[];
      countries?: string[];
      caseCountryCodes?: string[];
      caseRegionIds?: string[];
      currentGlobalLocationFilter: GlobalFilterLocation;
      locationFilterCategory: LocationFilterCategory;
      time?: {
        timeOption: string;
        customTimeframe?: TimeframeInput;
      };
      topDepartments?: string[];
    };
    operational: {
      sites?: string[];
      countries?: string[];
      caseCountryCodes?: string[];
      caseRegionIds?: string[];
      currentGlobalLocationFilter: GlobalFilterLocation;
      locationFilterCategory: LocationFilterCategory;
      time?: {
        timeOption: string;
        customTimeframe?: TimeframeInput;
      };
    };
  };
  case: CaseInitialState;
  user: UserInitialState;
  reporting: {
    currentUser: User | null;
    createReportingSessionLoading: boolean;
    fetchReportingSessionLoading: boolean;
  };
};

export const initialState: State = {
  globalUsers: {
    currentUser: null,
    session: '',
    hash: '',
    usersUpdated: new Date(),
  },
  globalReports: {
    reports: [],
    currentReport: null,
    columnShowing: {
      type: true,
      dateReceived: true,
      status: true,
      assignee: true,
      reporter: true,
      source: true,
      substantiation: true,
    },
    messages: new Map(),
    auditLog: new Map(),
  },
  organisation: {
    organisation: null,
    isLegacyMode: false,
  },
  fetching: {
    isFetching: true,
  },
  features: [],
  login: {
    flow: LoginFlow.USERNAME,
    email: '',
  },
  openReporting: {},
  integrationStatus: {
    dataIntegration: Integration.Types.NONE,
    inviteNew: false,
    syncState: {
      state: Integration.SyncStates.NotSetup,
      completedAt: null,
      startedAt: null,
    },
    subscribeStatus: {
      subscribe: {
        name: null,
        department: null,
        completedAt: null,
      },
      auth: {
        name: null,
        department: null,
        completedAt: null,
      },
      consent: {
        name: null,
        department: null,
        completedAt: null,
      },
    },
  },
  manualCaseCreation: {
    mccReducer: {
      reportID: '',
      loading: true,
      notfound: true,
      saving: false,
      isSavedToDb: false,
      isExistingReport: false,
      currentReport: null,
      currentEvent: null,
    },
  },
  insightsGlobalFilters: {
    organisational: {
      currentGlobalLocationFilter: GlobalFilterLocation.SITES,
      locationFilterCategory: LocationFilterCategory.NAMED_OFFENDER,
    },
    operational: {
      currentGlobalLocationFilter: GlobalFilterLocation.SITES,
      locationFilterCategory: LocationFilterCategory.NAMED_OFFENDER,
    },
  },
  case: {
    caseById: null,
  },
  user: {
    organisationRoles: [],
  },
  reporting: {
    currentUser: null,
    createReportingSessionLoading: false,
    fetchReportingSessionLoading: false,
  },
};

export interface Session {
  account: {
    user: {
      id: string;
      organisation: {
        slug?: string | undefined;
        id: number;
        name: string;
        expiryPeriod: number;
        authenticationType?: AuthenticationType;
        activeScimProvider?: boolean;
        isLegacyMode?: boolean;
      };
      permissions: Array<PermissionsType>;
      systemPermissions?: string[];
      SystemRoles: { id: string; label: string; name: SystemRolesName }[];
    };
    username: string;
  };
  token: string;
  hash: string;
  userHash: string;
}

export enum REPORT_SOURCE {
  'app' = 'Mobile App',
  'open-reporting' = 'Open Reporting',
  'resolution-hub' = 'Resolution Hub',
  'manual-case-creation' = 'Manual Intake',
  'vault-talk' = 'VaultTalk',
  'slack' = 'Slack Intake',
  'imported' = 'Imported',
  'reporter-portal' = 'Vault',
  'unknown' = '',
}

export enum MCCTypes {
  RESET = 'RESET',
  REPORT_SUCCESS = 'REPORT_SUCCESS',
  ADD_REPORT = 'ADD_REPORT',
  REPORT_FAILURE = 'REPORT_FAILURE',
  FETCH_ONE = 'FETCH_ONE',
  UPDATE = 'UPDATE',
  UPDATE_SUCCESS = 'UPDATE_SUCCESS',
  UPDATE_FAILURE = 'UPDATE_FAILURE',
  UPDATE_LOCAL_DRAFT = 'UPDATE_LOCAL_DRAFT',
  SAVE_DRAFT = 'SAVE_DRAFT',
  SAVE_DRAFT_SUCCESS = 'SAVE_DRAFT_SUCCESS',
  SAVE_DRAFT_FAILURE = 'SAVE_DRAFT_FAILURE',
  INIT_LOCAL_DRAFT = 'INIT_LOCAL_DRAFT',
  EDIT_EVENT = 'EDIT_EVENT',
  ADD_EVENT = 'ADD_EVENT',
  SAVE_EMAIL = 'SAVE_EMAIL',
  SAVE_EMAIL_FAILURE = 'SAVE_EMAIL_FAILURE',
  ADD_REPORTED_CATEGORY = 'ADD_REPORTED_CATEGORY',
  ADD_DETERMINED__CATEGORY = 'ADD_DETERMINED__CATEGORY',
  ADD_MI_REPORT_SOURCE = 'ADD_MI_REPORT_SOURCE',
}

export type MCCAction =
  | {
      type: MCCTypes.RESET;
    }
  | {
      type: MCCTypes.REPORT_SUCCESS;
      payload: { reportID: string; caseID?: string };
    }
  | {
      type: MCCTypes.REPORT_FAILURE;
      payload: { error: string };
    }
  | {
      type: MCCTypes.ADD_REPORT;
      payload: { assignee: number };
    }
  | {
      type: MCCTypes.FETCH_ONE;
      payload: { currentReport: UpdateReport };
    }
  | {
      type: MCCTypes.UPDATE;
    }
  | {
      type: MCCTypes.UPDATE_SUCCESS;
      payload: { reportID: string; currentReport: UpdateReport };
    }
  | {
      type: MCCTypes.UPDATE_FAILURE;
      payload: { error: string };
    }
  | {
      type: MCCTypes.EDIT_EVENT;
      payload: { currentEvent: MCCEventType };
    }
  | {
      type: MCCTypes.SAVE_EMAIL;
      payload: { contactEmail: string };
    }
  | {
      type: MCCTypes.SAVE_EMAIL_FAILURE;
      payload: { error: string };
    }
  | {
      type: MCCTypes.ADD_REPORTED_CATEGORY;
      payload: { reportedCategory?: CategoryDisplayType };
    }
  | {
      type: MCCTypes.ADD_DETERMINED__CATEGORY;
      payload: { determinedCategory?: CategoryDisplayType };
    }
  | {
      type: MCCTypes.ADD_MI_REPORT_SOURCE;
      payload: { miReportSource?: MiReportSource };
    }
  | {
      type: MCCTypes.UPDATE_LOCAL_DRAFT;
      payload: { currentReport: UpdateReport };
    }
  | {
      type: MCCTypes.INIT_LOCAL_DRAFT;
      payload: { isExistingReport: boolean };
    }
  | {
      type: MCCTypes.SAVE_DRAFT;
    }
  | {
      type: MCCTypes.SAVE_DRAFT_SUCCESS;
      payload: { reportID: string; caseID?: string; responseId?: string };
    }
  | {
      type: MCCTypes.SAVE_DRAFT_FAILURE;
      payload: { error: string };
    };

export interface MCCFile {
  id: string;
  created: string;
  updated: string;
  path: string;
  status: UploadScanStatus | null;
}

export interface MCCEventType {
  id: string;
  date: { value: string; created: string; updated: string };
  time: { value: string; created: string; updated: string };
  location: { value: string; created: string; updated: string };
  description: {
    value: string;
    created: string;
    updated: string;
  };
  emotion: { value: string; created: string; updated: string };
  witness: { value: string; created: string; updated: string };
  files: MCCFile[];
  perpetrators: {
    external?: boolean;
    id?: string;
    name: string;
    created: string;
    updated: string;
  }[];
  created?: string;
  updated?: string;
  timestamp?: string;
}

export interface TextBoxProps {
  placeholder: string;
  id: string;
  value: string;
  valid: boolean;
  onChange:
    | ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
    | undefined;
  // allowing any type as TextBox component will soon be deprecated
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  classes?: any;
  password?: boolean;
  search?: boolean;
  isWhite?: boolean;
  showHelpertext?: boolean;
  'data-testid'?: string;
  autoComplete?: string;
  style?: React.CSSProperties;
  // eslint-disable-next-line @typescript-eslint/ban-types
  enterKeyAction?: Function;
  disabled?: boolean;
}

export const IMAGE_MIME_TYPES = [
  'image/png',
  'image/apng',
  'image/jpg',
  'image/jpeg',
  'image/pjpeg',
  'image/svg+xml',
  'image/svg',
  'image/gif',
  'image/tiff',
  'image/bmp',
  'image/vnd.adobe.photoshop',
  'image/avif',
];

export const mapCsvRecordToBulkImportCasesDynamic = (
  record: BulkImportCasesDynamic & { $custom?: Record<string, unknown> },
): BulkImportCasesDynamic => {
  const { $custom, custom: existingCustom, ...caseFields } = record;
  const custom = existingCustom ?? $custom;
  return custom !== undefined ? { ...caseFields, custom } : caseFields;
};

export type CsvImportSuccess = {
  template_key: string;
  workspace_id: number;
  workspace_metadata: object;
  sheet_id: number;
  sheet_metadata: {
    original_file_name: string;
  };
  columns: {
    sheet_column_name: string;
    template_column_name?: string;
    template_column_key?: string;
    is_multi_mappable?: boolean;
    is_custom?: boolean;
    is_unmapped?: boolean;
    custom_column_name?: string;
  }[];
  count: number;
  records:
    | BulkReinviteCreateUser[]
    | BulkImportCases[]
    | BulkImportCasesDynamic[];
  error_records: {
    data: BulkReinviteCreateUser | BulkImportCases | BulkImportCasesDynamic;
    errors: Record<string, number[]>;
  }[];
  embed_id: number;
};

export enum TranscriptLanguages {
  Afrikaans = 'af',
  Albanian = 'sq',
  Amharic = 'am',
  Arabic = 'ar',
  Armenian = 'hy',
  Azerbaijani = 'az',
  Bengali = 'bn',
  Bosnian = 'bs',
  Bulgarian = 'bg',
  Catalan = 'ca',
  Chinese_Simplified = 'zh',
  Chinese_Traditional = 'zh-TW',
  Croatian = 'hr',
  Czech = 'cs',
  Danish = 'da',
  Dari = 'fa-AF',
  Dutch = 'nl',
  English = 'en-GB',
  Estonian = 'et',
  Farsi_Persian = 'fa',
  Filipino_Tagalog = 'tl',
  Finnish = 'fi',
  French = 'fr',
  French_Canada = 'fr-CA',
  Georgian = 'ka',
  German = 'de',
  Greek = 'el',
  Gujarati = 'gu',
  Haitian_Creole = 'ht',
  Hausa = 'ha',
  Hebrew = 'he',
  Hindi = 'hi',
  Hungarian = 'hu',
  Icelandic = 'is',
  Indonesian = 'id',
  Irish = 'ga',
  Italian = 'it',
  Japanese = 'ja',
  Kannada = 'kn',
  Kazakh = 'kk',
  Korean = 'ko',
  Latvian = 'lv',
  Lithuanian = 'lt',
  Macedonian = 'mk',
  Malay = 'ms',
  Malayalam = 'ml',
  Maltese = 'mt',
  Marathi = 'mr',
  Mongolian = 'mn',
  Norwegian_Bokmål = 'no',
  Pashto = 'ps',
  Polish = 'pl',
  Portuguese_Brazil = 'pt',
  Portuguese_Portugal = 'pt-PT',
  Punjabi = 'pa',
  Romanian = 'ro',
  Russian = 'ru',
  Serbian = 'sr',
  Sinhala = 'si',
  Slovak = 'sk',
  Slovenian = 'sl',
  Somali = 'so',
  Spanish = 'es',
  Spanish_Mexico = 'es-MX',
  Swahili = 'sw',
  Swedish = 'sv',
  Tamil = 'ta',
  Telugu = 'te',
  Thai = 'th',
  Turkish = 'tr',
  Ukrainian = 'uk',
  Urdu = 'ur',
  Uzbek = 'uz',
  Vietnamese = 'vi',
  Welsh = 'cy',
}

export enum FormState {
  DEFAULT = 'default',
  DISABLED = 'disabled',
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  READONLY = 'readonly',
  INFO = 'info',
}

export enum FormInputVariant {
  INPUT = 'TextInput',
  AREA = 'TextArea',
  DROPDOWN = 'Dropdown',
}

export enum HumanReadableLanguages {
  'ar-AR' = 'Arabic',
  'bg-BG' = 'Bulgarian',
  'cs-CS' = 'Czech',
  'cs-CH' = 'Czech',
  'de-DE' = 'German',
  'en-GB' = 'English',
  'en-US' = 'English',
  'es-ES' = 'Spanish',
  'fi-FI' = 'Finnish',
  'fr-FR' = 'French',
  'he-IL' = 'Hebrew',
  'hi-IN' = 'Hindi',
  'hu-HU' = 'Hungarian',
  'id-ID' = 'Indonesian',
  'it-IT' = 'Italian',
  'ja-JA' = 'Japanese',
  'ja-JP' = 'Japanese',
  'ko-KO' = 'Korean',
  'ko-KR' = 'Korean',
  'nl-BE' = 'Dutch (Belgium)',
  'nl-NL' = 'Dutch (Netherlands)',
  'no-NO' = 'Norwegian',
  'pl-PL' = 'Polish',
  'pt-BR' = 'Portuguese (Brazil)',
  'pt-PT' = 'Portuguese (Portugal)',
  'ro-RO' = 'Romanian',
  'ru-RU' = 'Russian',
  'sk-SK' = 'Slovak',
  'sl-SL' = 'Slovenian',
  'sv-SE' = 'Swedish',
  'th-TH' = 'Thai',
  'tr-TR' = 'Turkish',
  'uk' = 'Ukrainian',
  'ur-PK' = 'Urdu',
  'vi' = 'Vietnamese',
  'zh-CN' = 'Chinese (Simplified)',
}

const IGNORE_LANGUAGES_FOR_CATEGORIES: Array<
  keyof typeof HumanReadableLanguages
> = ['cs-CH', 'ja-JP', 'ko-KR', 'en-US'];

export const HumanReadableLanguagesForCategories = Object.entries(
  HumanReadableLanguages,
).filter(
  ([locale]) =>
    !IGNORE_LANGUAGES_FOR_CATEGORIES.includes(
      locale as keyof typeof HumanReadableLanguages,
    ),
);

export enum RoutingDataSource {
  REPORTER = 'Reporter data',
  OFFENDER = 'Named offender',
  REPORT = 'Report',
}

export enum PhoneLineType {
  MOBILE = 'mobile',
  LANDLINE = 'landline',
  FIXED_VOIP = 'fixedVoip',
  NON_FIXED_VOIP = 'nonFixedVoip',
  PERSONAL = 'personal',
  TOLL_FREE = 'tollFree',
  PREMIUM = 'premium',
  SHARED_COST = 'sharedCost',
  UAN = 'uan',
  VOICEMAIL = 'voicemail',
  PAGER = 'pager',
  UNKNOWN = 'unknown',
}

export type ModalActionBarHelpText = {
  text: string;
  messageState: FormState;
  title: string;
};

export enum AppStatus {
  INVITED = 'INVITED',
  EXPIRING = 'EXPIRING',
  DEACTIVATED = 'DEACTIVATED',
  NOT_INVITED = 'NOT INVITED',
}

export enum DateType {
  CompanyCreatedAt = 'companyCreatedAt',
  ReadAt = 'readAt',
  InvestigatingdAt = 'investigatingAt',
  ClosedAt = 'closedAt',
}

export enum NamedOffenderTypeForDisplay {
  TIME_OF_SUBMISSION = 'Time of submission',
  POST_SUBMISSION = 'Post submission',
}

export enum CaseTeamMemberRole {
  TEAM = 'Team',
  LEAD = 'Lead',
}

export enum CaseTypeEnum {
  COI = 'coi',
  SPEAK_UP = 'speak-up',
}
export enum SubmissionModeForDisplay {
  GO_TOGETHER = 'Mobile App - GoTogether',
  GO_AHEAD = 'Mobile App - Go Ahead',
  GO_ANONYMOUS = 'Mobile App - Go Anonymous',
  OPEN_REPORTING = 'Open Reporting',
  MANUAL_INTAKE = 'Manual Intake',
  VAULT_TALK = 'VaultTalk',
  UNKNOWN = 'Unknown',
  SLACK_GO_AHEAD = 'Slack - Go Ahead',
  SLACK_GO_ANONYMOUS = 'Slack - Go Anonymous',
  BULK_IMPORT = 'Imported',
  COI = 'Conflict of Interest Form',
  REPORTER_PORTAL = 'Vault',
  REPORTER_PORTAL_GO_TOGETHER = 'Vault - GoTogether',
}

export enum MiReportSourceForDisplay {
  PHONE = 'Phone',
  EMAIL = 'Email',
  POSTAL = 'Postal',
  BOX = 'Suggestion box',
  FACE_TO_FACE = 'Face to face',
  COLLABORATION_APP = 'MS Teams/Slack',
  VIDEO_CONFERENCING = 'Video conferencing',
  OTHER = 'Other',
  UNSPECIFIED = 'Unspecified',
}

export type CasePermissionsType =
  | 'VIEW_CASE'
  | 'VIEW_CASE_ON_CASES_TABLE'
  | 'VIEW_CASE_DETAILS'
  | 'EDIT_CASE_DETAILS'
  | 'VIEW_CASE_WORKSPACE_TAB'
  | 'EDIT_CASE_WORKSPACE_TAB'
  | 'VIEW_CASE_AUDIT_LOGS_TAB'
  | 'VIEW_REPORT_DETAILS'
  | 'EDIT_REPORT_DETAILS'
  | 'VIEW_REPORT_MESSAGES'
  | 'SEND_REPORT_MESSAGES'
  | 'VIEW_CASE_DETAILS_PII'
  | 'VIEW_REPORT_DETAILS_PII'
  | 'VIEW_CASE_PII'
  | 'VIEW_REPORT_PII'
  | 'EDIT_CASE_AUDIT_LOGS_TAB';

export type SystemPermissionType = 'ASSIGNABLE_TO_CASE_TEAM';

export type CaseTeamMemberPermissionsType =
  | CasePermissionsType
  | SystemPermissionType;

export type TeamMemberStatus = 'Active' | 'Deactivated' | '-';
/**
 * Utility functions
 */
export type Nullable<T> = T | null;

export type Option = {
  label: string | number;
  value: string | number;
};

export enum InsightsDashboard {
  ORGANISATIONAL = 'organisational',
  OPERATIONAL = 'operational',
}

export enum GlobalFilterProps {
  LOCATION = 'location',
  TIME = 'time',
}
export interface ResHubLoginNavigationState {
  redirectToNext?: string;
}

export type TranslationKey = ParseKeys;

export type Translation = {
  translation: {
    Buttons: {
      back: string;
      changePassword: string;
      copiedVaultId: string;
      copyVaultID: string;
      create: string;
      next: string;
      notSure: string;
      submit: string;
      signUp: string;
      updatePassword: string;
    };
    Errors: {
      createReportFailed: string;
    };
    ReportStatus: {
      CLOSED: string;
      INVESTIGATING: string;
      NEW: string;
      READ: string;
    };
    general: {
      time: {
        day: string;
        days: string;
        hour: string;
        hours: string;
        minute: string;
        minutes: string;
        second: string;
        seconds: string;
      };
      navbar: {
        reporter: {
          myReports: string;
        };
        settings: string;
        title: string;
        caseSettings: string;
        changePassword: string;
        privacyStatement: string;
        logOut: string;
        technicalSupport: string;
        notifications: string;
        notificationsDrawer: {
          caseAssigned: string;
          reportAssigned: string;
          newReport: string;
          staleCase: string;
          newMessage: string;
          caseTeamInvite: string;
          permissionRemoved: string;
          postSSubmissionFile: string;
          reportStatusChange: string;
          markAllAsRead: string;
          emptyTitle: string;
          emptyDescription: string;
          report: string;
          case: string;
          reporterAnonymised: string;
          dataSanitisation: string;
          reporterReportSubmitted: {
            title: string;
            subtitle: string;
          };
          learnMore: string;
          appReportReassigned: string;
          appReportMatch: string;
          appFileScanFailed: string;
          caseLinked: string;
          caseUnlinked: string;
          caseImporterJobCompleted: {
            success: string;
            PartialSuccess: string;
            failure: string;
          };
          importId: string;
          mitigationPlanReminder: string;
        };
      };
    };
    public: {
      ChatContainer: {
        backToMessages: string;
        messagePlaceholder: string;
        showMoreBanner?: string;
        originalMessages?: string;
        translatedMessages?: string;
        title?: string;
        titlev2?: string;
        description?: string;
        autoTranslate?: string;
        messageSettings?: string;
        caseSettings?: string;
        descriptionSecondHalf?: string;
        understood?: string;
        changeInProfile?: string;
        anonymousLabel: string;
        translatedMessageLabel: string;
        deletedByMessage?: string;
        deletedMessageLabel?: string;
      };
      MessageSettings?: {
        enableTranslationsHeader: string;
        enableTranslationsDescription: string;
        enableTranslationsSubDescription?: string;
        enableTranslationsSubDescriptionv2?: string;
        enableTranslationLabelEnabled: string;
        enableTranslationLabelDisabled: string;
        updateTranslationSuccess: string;
        updateTranslationFailed: string;
      };
      CreatePublicReport: {
        createReport: string;
        helpPage: string;
        loginToOpr: string;
        or: string;
        passwordPlaceholder: string;
        phoneNumberLoginFailed: string;
        phoneNumberPlaceholder: string;
        phoneNumberTooltip: string;
        pinPlaceholder: string;
        reportIntroductionParagraph1: string;
        reportIntroductionParagraph2: string;
        reportIntroductionParagraph3: string;
        reportIssue: string;
        usernameLoginFailed: string;
        vaultId: string;
        vaultIdPlaceholder: string;
        vaultIdTooltip: string;
        vaultTalk: string;
        yourPassword: string;
        yourPhoneNumber: string;
        yourPin: string;
        yourVaultId: string;
        validationError: {
          publicReport: {
            relationshipToCompanyRequired: string;
            reportedCategoryRequired: string;
          };
        };
      };
      PublicMoreEvidence: {
        header: string;
        description: string;
        backToReportId: string;
        additionalEvidenceHeader: string;
        vaultTalkOpenCaseHeader: string;
      };
      CustomHeader: {
        companyLogo: string;
        openReporting: string;
        userSettings: string;
      };
      Date: {
        approximateDescription: string;
        approximatePlaceholder: string;
        cancel: string;
        clear: string;
        description: string;
        header: string;
        ok: string;
        placeholder: string;
      };
      Description: {
        whatHappened: string;
        whatHappenedPlaceholder: string;
        whatHappenedText: string;
      };
      Details: {
        disclaimer: string;
        emailPlaceholder: string;
        fullNamePlaceholder: string;
        header: string;
        subtitle: string;
        nonAnonSubtitle: string;
        nonOptionalEmailPlaceholder: string;
        nonOptionalFullNamePlaceholder: string;
      };
      Emotion: {
        description: string;
        header: string;
        placeholder: string;
      };
      Evidence: {
        tableTitle: string;
        webErrorMessage: string;
        mobileErrorMessage: string;
        nameHeading: string;
        addedHeading: string;
        statusHeading: string;
        failedToOpenFileMessage: string;
        fileStatuses: {
          pendingUpload: string;
          passedUpload: string;
          readyForDownload: string;
          infectedUpload: string;
        };
        deletedStatus: string;
        deletedMessage: string;
      };
      FileUpload: {
        addFiles: string;
        uploadFiles?: string;
        addMoreFiles: string;
        confirm: string;
        description: string;
        dragDrop: string;
        fileLimit: string;
        header: string;
        helpArticle: string;
        highlighted: string;
        information: string;
        moreInformation: string;
        tooltipAudio: string;
        tooltipAudioOptions: string;
        tooltipDocuments: string;
        tooltipDocumentsOptions: string;
        tooltipImage: string;
        tooltipImageOptions: string;
        tooltipText: string;
        tooltipTextOptions: string;
        tooltipVideo: string;
        tooltipVideoOptions: string;
        fileUploadLimitText: string;
        errorMessage: string;
        uploadErrors: {
          invalidFileType: string;
          invalidFileSize: string;
          systemError: string;
          tooManyFiles: string;
        };
        postSubmissionErrorMessage: string;
      };
      Loading: {
        loading: string;
        postSubmissionLoading?: string;
      };
      Country: {
        description: string;
        header: string;
        noOptions: string;
        other: string;
        placeholder: string;
      };
      Site?: {
        description: string;
        header: string;
        noOptions: string;
        other: string;
        placeholder: string;
      };
      Location: {
        description: string;
        header: string;
        placeholder: string;
      };
      MessageListBox: {
        messageCaseManager: string;
        report: string;
      };
      Password: {
        changePasswordHeader: string;
        createNewPassword: string;
        createPasswordHeader: string;
        currentPasswordPlaceholder: string;
        description: string;
        newDescription?: string;
        enterCurrentPassword: string;
        passwordPlaceholder: string;
        reenterPlaceholder: string;
        subtitle: string;
        forgottenPasswordInstructions: string;
        yourVaultId: string;
        yourVaultIdPlaceholder: string;
        passwordResetRequestedHeader: string;
        passwordResetRequestedDescription: string;
      };
      Perpetrators: {
        description: string;
        header: string;
        placeholder: string;
      };
      PoweredByVault: string;
      PrivacyStatement: string | null;
      PublicMessageList: {
        myMessages: string;
      };
      PublicReport: {
        anonymous: string;
        backToReports: string;
        skipped: string;
      };
      PublicReportCard: {
        messageCaseManager: string;
        reportId: string;
        submitted: string;
        viewReport: string;
        addEvidence: string;
        vaultTalkOpenCaseCTA: string;
        vaultTalkClosedCaseCTA: string;
        viewAdditionalEvidence?: string;
      };
      PublicReportsList: {
        reportingSystem: string;
      };
      Question: {
        placeholder: string;
        question: string;
        questionText: string;
      };
      Relationship: {
        header: string;
        tellUsAbout: string;
      };
      ResponsiveNavMenu: {
        messages: string;
        reports: string;
      };
      SpamPuzzle: {
        dialog: string;
      };
      SubmissionTypes: {
        reportIssue: string;
        whatType: string;
      };
      SubmitSuccess: {
        recordYourVaultID: string;
        successMessage: string;
        successThankYou: string;
        successSanitationMessage: string;
        successSanitationTagline: string;
        successInformation: string;
        viewReport: string;
        category: string;
        country: string;
        dateSubmitted: string;
      };
      Time: {
        approximateDescription: string;
        approximatePlaceholder: string;
        description: string;
        header: string;
        invalidTime: string;
      };
      UserSettingsDialog: {
        changePassword: string;
        closeDialog: string;
        logout: string;
        userSettings: string;
        messageSettings?: string;
      };
      Witnesses: {
        description: string;
        header: string;
        placeholder: string;
      };
    };
    reporting: {
      ReportingLogin: {
        title: string;
        usernameLoginFailed: string;
        passwordResetRequired: string;
        passwordPlaceholder: string;
        reenterPlaceholder: string;
        newPasswordPlaceholder: string;
        passwordSuccessfullySetTitle: string;
        passwordSuccessfullySetMessage: string;
        passwordFailTitle: string;
        passwordFailMessage: string;
        createAccountCTA: string;
        tooltip: string;
        emailLabel: string;
        passwordLabel: string;
        loginButton: string;
        forgotPassword: string;
        accountLocked: string;
        accountLockedPermanently: string;
        accountFrozen: string;
        currentPassword: string;
      };
      MessageSlideIn: {
        title: string;
        confirmButton: string;
        translationHeader: string;
      };
      ReportingConnect: {
        heading: string;
        listHeading: string;
        connectButton: string;
        cancelButton: string;
        disclaimer: string;
        successfulConnectionDescription: string;
        successfulConnectionHeading: string;
        unsuccessfulConnectionDescription: string;
        unsuccessfulConnectionHeading: string;
        permissionIdentity: {
          heading: string;
          extraInfo: string;
        };
        permissionSubmitReport: {
          heading: string;
          extraInfo: string;
        };
        permissionNotifications: {
          heading: string;
          extraInfo: string;
        };
        permissionOverview: {
          heading: string;
          extraInfo: string;
        };
      };
      ReportingActivate: {
        header: string;
        description: string;
        buttonText: string;
        emailInput: string;
        successHeading: string;
        successAccount: string;
        successEmail: string;
      };
      ReportingReset: {
        successHeading: string;
        successDescription: string;
        failureHeading: string;
        failureDescription: string;
      };
      ReportList: {
        yourReports: string;
      };
      ReportCard: {
        addEvidence: string;
        submissionModeVia: string;
        viewEvidence: string;
      };
      ReportingForgotPassword: {
        heading: string;
        description: string;
        sendLink: string;
        sendLinkSuccess: string;
        sendLinkSuccessDescription: string;
        sendLinkFailure: string;
        sendLinkFailureDescription: string;
        updatePasswordSuccess: string;
        passwordReuseError: string;
        passwordReuseErrorDescription: string;
        invalidTokenError: string;
        updatePasswordSuccessDescription: string;
      };
      noReports: {
        title: string;
        description: string;
        descriptionHelpArticle: string;
        errorTitle: string;
        errorDescription: string;
        errorDescriptionSecondary: string;
      };
      ReportingChangePassword: {
        updatePasswordCTA: string;
        header: string;
        description: string;
        toastMessage: string;
        toastDescription: string;
        back: string;
        invalidToken: string;
        passwordUpdated: string;
        passwordUpdatedDescription: string;
        passwordUsedBefore: string;
        passwordUsedBeforeDescription: string;
        errorToast: string;
        incorrectCurrentPassword: string;
      };
      ReportingAddEvidence: {
        descriptionHeader: string;
        descriptionText: string;
        emptyMessage: string;
      };
      settings: {
        title: string;
        messageSettings: {
          header: string;
          subHeader: string;
          autoTranslationTitle: string;
          autoTranslationDescription1: string;
          autoTranslationDescription2: string;
        };
        notificationSettings: {
          header: string;
          subHeader: string;
          emailTitle: string;
          emailDescription: string;
        };
        updatedSettingsSuccess: string;
        updatedSettingsFailure: string;
      };
    };
    validators: {
      passwordLength: string;
      passwordLowercase: string;
      passwordMissMatch: string;
      passwordNumeric: string;
      passwordUppercase: string;
    };
    validatorsNew: {
      passwordLength: string;
      passwordLowercase: string;
      passwordMissMatch: string;
      passwordNumeric: string;
      passwordUppercase: string;
      passwordSpecialCharacter: string;
    };
  };
};

export type OnClickEvent = React.MouseEvent<HTMLAnchorElement>;

export enum UploadScanStatus {
  PENDING = 'PENDING',
  CLEAN = 'CLEAN',
  INFECTED = 'INFECTED',
  MANUALLY_APPROVED = 'MANUALLY_APPROVED',
}

export type CaseLead = Maybe<Partial<UserType>> | undefined;

export enum UploadOperation {
  ADDED = 'added',
  REMOVED = 'removed',
}

export enum SubmissionType {
  TIME_OF_SUBMISSION = 'time-of-submission',
  POST_SUBMISSION = 'post-submission',
}

export enum Platform {
  REPORTING = 'reporting',
  OPR = 'opr',
}

export enum TagTypeStatus {
  LIVE = 'LIVE',
  PAUSED = 'PAUSED',
}

export enum TagStatus {
  LIVE = 'LIVE',
  PAUSED = 'PAUSED',
}

export enum USER_ROLE {
  REPORTER = 'reporter',
  CASE_LEAD = 'case lead',
  REPORT_ASSIGNEE = 'report assignee',
  SYSTEM_TEAM_MEMBER = 'system team member',
  CASE_TEAM_MEMBER = 'case team member',
}

export enum CaseLeadUpdateLocation {
  CASES_TABLE = 'cases-table',
  CASE_PAGE = 'case-page',
}

export enum NavbarVariantEnum {
  REPORTER = 'reporter',
  MANAGER = 'manager',
  PUBLIC = 'public',
}
export enum REPORT_SOURCE_TYPES {
  APP = 'app',
  OPR = 'open-reporting',
  SLACK = 'slack',
  MCC = 'manual-case-creation',
  PHONE = 'vault-talk',
  REPORTER_PORTAL = 'reporter-portal',
  UNKNOWN = 'unknown',
}
